// System Design track for Learn. Fundamentals first, then case studies that
// apply them. Each fundamentals lesson follows the Codeazy lesson standard:
// why it matters → mental model → explanation → trade-offs → common mistakes
// → interview questions → practice → key takeaways. Case studies follow the
// interview flow: requirements → estimates → entities and API → high-level
// design → deep dives → what each level is expected to cover.

import { systemDesignCore2 } from './systemDesignCore2';
import { systemDesignCore3 } from './systemDesignCore3';
import { systemDesignCaseStudies } from './systemDesignCases';
import { systemDesignCaseStudies2 } from './systemDesignCases2';
import { systemDesignCaseStudies3 } from './systemDesignCases3';

const fundamentals = [
  {
    slug: 'how-to-approach-system-design',
    title: 'How to Approach a System Design Interview',
    summary: 'A repeatable six-step method, how to manage the clock, and what interviewers expect at each level.',
    readTime: 12,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'System design interviews are open-ended on purpose. There is no single correct design, so you are judged on how you think: whether you understand what is being asked, make sensible decisions, explain trade-offs, and steer a 45-minute conversation without getting lost. Candidates who know plenty of technology still fail when they have no method. A simple, repeatable structure fixes that.' },

      { type: 'h2', text: 'The mental model: you are the tech lead in a design review' },
      { type: 'p', text: 'Imagine a product manager has walked into your room with a one-line idea, and you have 45 minutes to turn it into a plan the team could start building. You would not start drawing databases. You would ask what the product must do, how big it will get, sketch the main pieces, then zoom into the risky parts. That is exactly the flow the interview rewards.' },

      { type: 'h2', text: 'The six steps' },
      { type: 'table', headers: ['Step', 'Time (45 min)', 'What you produce'], rows: [
        ['1. Requirements', '~5 min', '3–5 core features, and what is out of scope'],
        ['2. Non-functional goals and estimates', '~5 min', 'Scale, latency, availability and consistency targets, with rough numbers'],
        ['3. Core entities', '~2 min', 'The main nouns: User, Post, Message, Order…'],
        ['4. API', '~5 min', 'The endpoints or events that deliver the core features'],
        ['5. High-level design', '~10 min', 'Services, data stores and the path of each core request'],
        ['6. Deep dives', '~15 min', 'The 2–3 hardest problems, solved with trade-offs']
      ] },

      { type: 'h3', text: 'Step 1: Functional requirements' },
      { type: 'p', text: 'Turn the vague prompt into a short list of things users can do. Write them as “users should be able to…” and agree on them with the interviewer. Then say explicitly what you will not cover; it shows judgement and saves time.' },
      { type: 'code', language: 'text', code: `Design a ride-hailing app

In scope
  1. Riders request a ride from A to B and see a price estimate
  2. Nearby drivers receive the request and can accept it
  3. Riders and drivers see each other's location during the trip

Out of scope (say it out loud)
  ratings, scheduled rides, payments internals, driver onboarding` },

      { type: 'h3', text: 'Step 2: Non-functional requirements and estimates' },
      { type: 'p', text: 'These are the qualities that shape the architecture. Pick the few that actually matter for this product and attach numbers.' },
      { type: 'ul', items: [
        '**Scale**: how many users and requests per second, and is it read-heavy or write-heavy?',
        '**Latency**: what must feel instant (matching a driver) and what can be slow (a receipt email)?',
        '**Availability vs consistency**: is a stale answer acceptable, or must every read be exact?',
        '**Durability**: can we ever lose this data?'
      ] },
      { type: 'p', text: 'Estimates exist to drive decisions, not to show arithmetic. Round aggressively: one day is about 100,000 seconds, so one million requests a day is roughly 12 per second.' },
      { type: 'callout', variant: 'tip', title: 'Make every number earn its place', text: 'After an estimate, say what it changes: “20,000 location updates per second is too many for one database to absorb, so I’ll keep live locations in memory.” A number that does not change a decision is not worth the time.' },

      { type: 'h3', text: 'Step 3: Core entities' },
      { type: 'p', text: 'List the main things the system stores. You do not need full schemas yet; names and a few key fields are enough to make the API and design concrete.' },

      { type: 'h3', text: 'Step 4: API' },
      { type: 'p', text: 'Write one endpoint (or event) per functional requirement. This keeps the design anchored to what users actually do, and quickly reveals missing pieces.' },
      { type: 'code', language: 'http', code: `POST /rides                 { pickup, dropoff }          → { rideId, estimate }
POST /rides/{id}/accept     (driver)                     → 200 | 409 already taken
PUT  /drivers/me/location   { lat, lng }                 → 204
GET  /rides/{id}            → ride status + driver location` },
      { type: 'tip', text: 'Never trust identity from the request body. The user or driver comes from the auth token, not from a `userId` field the client sends.' },

      { type: 'h3', text: 'Step 5: High-level design' },
      { type: 'p', text: 'Draw the simplest design that satisfies the functional requirements, then walk each core request through it. Do not optimise yet. A working simple design you can extend is worth more than a complex one you cannot explain.' },

      { type: 'h3', text: 'Step 6: Deep dives' },
      { type: 'p', text: 'Now revisit the non-functional goals and find where the simple design breaks: a hot spot, a race condition, a scale limit, a failure mode. For each, present two or three options, pick one, and say why. This is where most of the scoring happens, especially for senior roles.' },

      { type: 'h2', text: 'What interviewers expect at each level' },
      { type: 'table', headers: ['Level', 'What “good” looks like'], rows: [
        ['Junior / new grad', 'A clear, working high-level design and sound basics (APIs, databases, caching). The interviewer may guide the deep dives.'],
        ['Mid-level', 'Drives the whole flow with little help, and handles one or two deep dives well with clear trade-offs.'],
        ['Senior', 'Proactively finds the hard problems, compares options with real reasoning (failure modes, cost, operations), and adapts quickly to new constraints.'],
        ['Staff+', 'Reasons about the system’s evolution, organisational and operational concerns, and where to invest; goes very deep in an area of their choosing.']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Drawing boxes before agreeing on requirements.',
        'Spending ten minutes on estimates that never affect the design.',
        'Adding microservices, Kafka and Kubernetes in minute five “because it scales”.',
        'Staying silent while drawing. Narrate your reasoning.',
        'Defending a choice when the interviewer pushes back, instead of exploring the alternative.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'What should you do if you don’t know a technology the interviewer mentions?', answer: ['Say so honestly, then reason from first principles: what properties do we need here, and what kind of component provides them? Interviewers care more about the reasoning than the product name.'] },
      { type: 'qa', question: 'How do you handle an interviewer who keeps changing requirements?', answer: ['That is usually deliberate. Restate the new requirement, identify which part of the design it affects, and adjust that part while keeping the rest. It shows you can evolve a design rather than restart.'] },
      { type: 'qa', question: 'How detailed should the database schema be?', answer: ['Enough to support the core queries: the main tables or collections, primary keys, and the fields you filter or sort on. Full column lists are rarely worth the time.'] },

      { type: 'exercise', title: 'Run the first two steps', task: 'For the prompt “Design a food delivery app”, write 3–4 functional requirements, an out-of-scope list, and three non-functional requirements with rough numbers. Then write one sentence for each number explaining what it changes in the design.', solution: `In scope
  1. Customers browse nearby restaurants and menus
  2. Customers place and pay for an order
  3. Restaurants accept orders; a delivery partner is assigned
  4. Customers track the delivery partner live

Out of scope: reviews, promotions, restaurant onboarding

Non-functional
  - 2M orders/day ≈ 25 orders/s average, ~150/s at dinner peak
      → a single primary database can handle order writes at first
  - Browsing is ~50x reads vs orders
      → cache menus and restaurant lists aggressively
  - Location updates every 4s from 100k active partners ≈ 25k/s
      → keep live locations in memory, not in the main database`, language: 'text' },

      { type: 'summary', items: [
        'Requirements → estimates → entities → API → high-level design → deep dives.',
        'Agree on scope first and say what is out of scope.',
        'Use numbers only to drive decisions.',
        'Build a simple working design, then fix where it breaks.',
        'Deep dives with clear trade-offs are where senior candidates stand out.'
      ] }
    ]
  },
  {
    slug: 'scaling-basics',
    title: 'Scaling Basics',
    summary: 'Vertical vs horizontal scaling, load balancers, stateless services and finding the real bottleneck.',
    readTime: 10,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'Almost every design question eventually asks “what happens when traffic grows 10x?”. Knowing how systems add capacity, and which part usually breaks first, lets you answer with a plan instead of a list of buzzwords.' },

      { type: 'h2', text: 'The mental model: a busy restaurant' },
      { type: 'p', text: 'One chef can only cook so fast. You can hire a faster chef with a bigger kitchen (vertical scaling), or hire more chefs and a host at the door who seats customers evenly (horizontal scaling with a load balancer). More chefs only help if any chef can cook any dish, which is why stateless services matter.' },

      { type: 'h2', text: 'Vertical vs horizontal scaling' },
      { type: 'table', headers: ['', 'Vertical (scale up)', 'Horizontal (scale out)'], rows: [
        ['How', 'Bigger machine: more CPU, RAM, faster disks', 'More machines sharing the load'],
        ['Effort', 'Almost none', 'App must be designed for it (statelessness, shared data)'],
        ['Limit', 'The biggest machine you can buy', 'Very high'],
        ['Failure', 'One machine is a single point of failure', 'Losing one machine reduces capacity, not availability'],
        ['Good for', 'Databases early on, quick wins', 'Web and API tiers, very large scale']
      ] },
      { type: 'tip', text: 'Vertical scaling is underrated. Modern servers are very large, and scaling a database up is often far simpler than sharding it. Say so in interviews before reaching for complexity.' },

      { type: 'h2', text: 'Load balancers' },
      { type: 'p', text: 'A load balancer sits in front of a group of servers, spreads requests across them, and stops sending traffic to servers that fail health checks.' },
      { type: 'table', headers: ['Strategy', 'How it picks a server', 'When it fits'], rows: [
        ['Round robin', 'Takes turns', 'Similar servers, similar requests'],
        ['Least connections', 'Fewest active connections', 'Requests of very different lengths, WebSockets'],
        ['Weighted', 'Proportional to server capacity', 'Mixed hardware, gradual rollouts'],
        ['Hash-based', 'Same key always goes to the same server', 'Caching per server, sticky sessions']
      ] },
      { type: 'p', text: 'Layer 4 load balancers route by IP and port and are very fast. Layer 7 load balancers understand HTTP, so they can route `/api` and `/images` to different services, terminate TLS, and retry failed requests.' },

      { type: 'h2', text: 'Stateless services' },
      { type: 'p', text: 'A service is stateless when any instance can handle any request because nothing important lives in that instance’s memory. That is what makes adding and removing servers safe.' },
      {"type": "diagram", "title": "Scaling out behind a load balancer", "caption": "Any app server can handle any request because sessions live in a shared store and data lives in the database. Adding a server is just adding another box behind the load balancer.", "cols": 4, "rows": 3, "nodes": [{"id": "client", "label": "Users", "kind": "client", "col": 0, "row": 1}, {"id": "lb", "label": "Load balancer", "kind": "edge", "col": 1, "row": 1, "sub": "health checks"}, {"id": "s1", "label": "App server 1", "kind": "service", "col": 2, "row": 0}, {"id": "s2", "label": "App server 2", "kind": "service", "col": 2, "row": 1}, {"id": "s3", "label": "App server 3", "kind": "service", "col": 2, "row": 2}, {"id": "sess", "label": "Session store", "kind": "cache", "col": 3, "row": 0, "sub": "Redis"}, {"id": "db", "label": "Database", "kind": "db", "col": 3, "row": 2}], "edges": [{"from": "client", "to": "lb"}, {"from": "lb", "to": "s1"}, {"from": "lb", "to": "s2"}, {"from": "lb", "to": "s3"}, {"from": "s1", "to": "sess"}, {"from": "s2", "to": "sess"}, {"from": "s2", "to": "db"}, {"from": "s3", "to": "db"}]},
      { type: 'compare', badLabel: 'Stateful', bad: `// Sessions in server memory:
// the next request may hit another
// server and the user is "logged out"
const sessions = {};
app.post('/login', (req, res) => {
  sessions[id] = user;
});`, goodLabel: 'Stateless', good: `// Session data in a shared store
// (or a signed token), so any
// server can handle the next request
app.post('/login', async (req, res) => {
  await redis.set(\`session:\${id}\`,
    JSON.stringify(user), 'EX', 86400);
});`, text: 'Move sessions, uploaded files, caches and scheduled jobs out of individual servers before scaling out.' },

      { type: 'h2', text: 'Finding the real bottleneck' },
      { type: 'p', text: 'Adding API servers does nothing if the database is the limit. Typical order in which things break as a product grows:' },
      { type: 'ol', items: [
        'The database (slow queries, then too many writes or connections).',
        'Hot data that every request reads (fixed with caching).',
        'Slow work done inside requests (fixed with queues and background workers).',
        'Network distance to users (fixed with CDNs and regional deployments).'
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Scaling the web tier while the database is the bottleneck.',
        'Keeping state in server memory and then adding servers.',
        'Forgetting that the load balancer itself must be redundant.',
        'Autoscaling on CPU for workloads that are actually waiting on I/O.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'What is the difference between vertical and horizontal scaling?', answer: ['Vertical scaling adds resources to one machine; it is simple but has a ceiling and a single point of failure. Horizontal scaling adds machines; it scales much further and survives failures, but requires stateless services and shared data stores.'] },
      { type: 'qa', question: 'Why do sticky sessions cause problems?', answer: ['Load becomes uneven because some users are much more active than others, and when a server dies its users lose their sessions. Moving session state to a shared store or signed tokens avoids both.'] },
      { type: 'qa', question: 'How would you scale a service from 1,000 to 100,000 requests per second?', answer: ['Measure where time goes first. Typically: make the API stateless and scale it horizontally behind a load balancer; add caching for hot reads; add database read replicas and indexes; move slow work to queues; use a CDN for static content; and shard the database only when writes or data size require it.'] },

      { type: 'exercise', title: 'Spot the scaling blockers', task: 'An app stores user sessions in memory, saves uploaded profile pictures to the server’s local disk, and runs a nightly report job with `setInterval` inside the API process. List what breaks when you run 5 instances behind a load balancer, and the fix for each.', solution: `Sessions in memory   → users appear logged out on other instances
                       Fix: Redis session store or signed tokens (JWT)
Local disk uploads   → files exist only on one instance
                       Fix: object storage (S3) + CDN
Job inside API       → the report runs 5 times
                       Fix: one scheduler or a queue with a single consumer/lock`, language: 'text' },

      { type: 'summary', items: [
        'Scale up first when it is simpler; scale out for large scale and resilience.',
        'Load balancers spread traffic and remove unhealthy servers.',
        'Stateless services are what make horizontal scaling safe.',
        'Always find and fix the actual bottleneck, usually the database first.'
      ] }
    ]
  },
  {
    slug: 'caching',
    title: 'Caching',
    summary: 'Where to cache, cache-aside and write strategies, invalidation, eviction and the failure modes that bite in production.',
    readTime: 11,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'Most products read data far more often than they write it. Caching lets you answer those repeated reads from fast memory instead of the database, which cuts latency and protects the database from load. It is one of the first tools to reach for, and one of the easiest to get subtly wrong.' },

      { type: 'h2', text: 'The mental model: the notes on your desk' },
      { type: 'p', text: 'Looking something up in the library (the database) takes a while. So you keep copies of what you use most on your desk (the cache). It is much faster, but the desk is small, and if the library’s copy changes, your notes can be out of date. Every caching decision is about desk space and staleness.' },

      { type: 'h2', text: 'Where caches live' },
      { type: 'table', headers: ['Layer', 'Example', 'Best for'], rows: [
        ['Browser', 'HTTP cache headers', 'Static assets, user-specific data that rarely changes'],
        ['CDN', 'Edge servers near users', 'Images, video, public pages and API responses'],
        ['Application, in-process', 'A small in-memory map per server', 'Tiny, very hot, rarely changing data (config)'],
        ['Distributed cache', 'Redis, Memcached', 'Shared query results, sessions, counters'],
        ['Database', 'Buffer pool, materialised views', 'Handled by the database itself']
      ] },

      { type: 'h2', text: 'Cache-aside (lazy loading)' },
      { type: 'p', text: 'The most common pattern: the application checks the cache, falls back to the database on a miss, and fills the cache with the result.' },
      {"type": "diagram", "title": "Cache-aside", "caption": "The app checks the cache first. On a miss it reads the database and stores the result in the cache with a TTL; on writes it updates the database and deletes the cache entry.", "cols": 3, "rows": 2, "nodes": [{"id": "client", "label": "Client", "kind": "client", "col": 0, "row": 0}, {"id": "app", "label": "App server", "kind": "service", "col": 1, "row": 0}, {"id": "cache", "label": "Redis", "kind": "cache", "col": 2, "row": 0, "sub": "TTL + LRU"}, {"id": "db", "label": "Database", "kind": "db", "col": 1, "row": 1}], "edges": [{"from": "client", "to": "app"}, {"from": "app", "to": "cache", "label": "1. get · 3. set", "both": true}, {"from": "app", "to": "db", "label": "2. on miss"}]},
      { type: 'code', variants: [
        { language: 'java', code: `public Course getCourse(long id) {
    String key = "course:v1:" + id;
    String cached = redis.get(key);
    if (cached != null) return gson.fromJson(cached, Course.class);    // hit

    Course course = courseRepository.findById(id).orElse(null);        // miss
    if (course != null) redis.setex(key, 600, gson.toJson(course));
    return course;
}

public Course updateCourse(long id, CourseChanges changes) {
    Course course = courseService.update(id, changes);
    redis.del("course:v1:" + id);                                       // invalidate after the write
    return course;
}` },
        { language: 'python', code: `import json

def get_course(course_id):
    key = f"course:v1:{course_id}"
    cached = redis.get(key)
    if cached:
        return json.loads(cached)                      # hit

    course = db.courses.find_by_id(course_id)          # miss
    if course:
        redis.set(key, json.dumps(course), ex=600)
    return course

def update_course(course_id, changes):
    course = db.courses.update(course_id, changes)
    redis.delete(f"course:v1:{course_id}")             # invalidate after the write
    return course` },
        { language: 'javascript', code: `async function getCourse(id) {
  const key = \`course:v1:\${id}\`;
  const cached = await redis.get(key);
  if (cached) return JSON.parse(cached);              // hit

  const course = await db.courses.findById(id);       // miss
  if (course) await redis.set(key, JSON.stringify(course), 'EX', 600);
  return course;
}

async function updateCourse(id, changes) {
  const course = await db.courses.update(id, changes);
  await redis.del(\`course:v1:\${id}\`);                // invalidate after the write
  return course;
}` }
      ] },

      { type: 'h2', text: 'Write strategies' },
      { type: 'table', headers: ['Strategy', 'How writes work', 'Trade-off'], rows: [
        ['Cache-aside + invalidate', 'Write the database, delete the cache key', 'Simple and safe; next read is a miss'],
        ['Write-through', 'Write cache and database together', 'Cache always warm; slower writes'],
        ['Write-behind', 'Write cache now, database later', 'Fast writes; risk of losing data'],
        ['Write-around', 'Write only the database', 'Avoids caching data nobody reads']
      ] },
      { type: 'tip', text: 'Prefer deleting the cache entry over updating it after a write. With concurrent writes, two “update cache” operations can arrive in the wrong order and leave old data cached until it expires.' },

      { type: 'h2', text: 'Eviction and expiry' },
      { type: 'ul', items: [
        '**TTL** puts an upper bound on staleness: “at most 10 minutes old”.',
        '**LRU** evicts what was used least recently; a solid default.',
        '**LFU** evicts what is used least often; good when popularity is stable.',
        'Set a memory limit and an eviction policy, otherwise the cache becomes an outage.'
      ] },

      { type: 'h2', text: 'Failure modes' },
      { type: 'table', headers: ['Problem', 'What happens', 'Fix'], rows: [
        ['Stampede', 'A hot key expires and thousands of requests hit the database at once', 'Let one request rebuild it (lock), serve stale while refreshing'],
        ['Avalanche', 'Many keys expire at the same moment', 'Add random jitter to TTLs'],
        ['Penetration', 'Requests for data that does not exist always miss', 'Cache “not found” briefly'],
        ['Hot key', 'One key overloads one cache node', 'Small local cache in each server for that key, or replicate it'],
        ['Stale reads', 'Cache disagrees with the database', 'Invalidate on write, short TTLs']
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Adding a cache with no plan for invalidation.',
        'Caching user-specific data under keys that do not include the user.',
        'Treating the cache as the source of truth.',
        'Caching before checking for a missing database index.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'What is a cache stampede and how do you prevent it?', answer: ['When a popular entry expires, many requests miss at once and all query the database. Prevent it by letting a single request rebuild the entry (using a short lock) while others wait or receive the stale value, by refreshing hot keys before they expire, and by adding jitter to TTLs.'] },
      { type: 'qa', question: 'When should you not cache?', answer: ['When data must always be exactly current (a payment balance during checkout), when it is rarely reused, or when the real problem is a slow query that an index would fix.'] },
      { type: 'qa', question: 'How do you cache personalised pages?', answer: ['Cache the shared parts (product data, layout) broadly, and the personalised parts per user with keys that include the user ID and short TTLs, or compose them at request time.'] },

      { type: 'exercise', title: 'Design the cache for a product page', task: 'A product page shows product details (changes a few times a day), price (changes during sales) and stock (changes constantly). Decide what to cache, where, with which TTL, and how to invalidate each.', solution: `Product details → Redis + CDN, TTL 1 hour, delete key on product update
Price          → Redis, TTL 1–5 min, delete key when a price changes
Stock          → don't cache the exact number; read live, or cache
                 "in stock / low / out" for a few seconds
Checkout       → always re-check price and stock from the database`, language: 'text' },

      { type: 'summary', items: [
        'Cache where reads repeat: browser, CDN, application, distributed cache.',
        'Cache-aside with delete-on-write is the safe default.',
        'TTLs bound staleness; eviction policies bound memory.',
        'Plan for stampedes, avalanches, hot keys and stale reads.'
      ] }
    ]
  },
  {
    slug: 'databases-and-indexing',
    title: 'Databases, Data Modelling and Indexes',
    summary: 'Choosing SQL vs NoSQL, modelling data around queries, and the indexes that keep it fast.',
    readTime: 12,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'Every design stores data, and the database choice and data model decide what is easy, what is fast, and what is impossible later. In interviews, a clear justification for your database and your main queries is a strong signal; “I’d use MongoDB because it scales” is a weak one.' },

      { type: 'h2', text: 'The mental model: design for the questions you will ask' },
      { type: 'p', text: 'A database is shaped by the questions you ask it most often. Start from the main read and write patterns (“load a user’s last 20 orders”, “find drivers near a point”), then choose a model and indexes that answer those questions cheaply.' },

      { type: 'h2', text: 'Relational vs non-relational' },
      { type: 'table', headers: ['Family', 'Examples', 'Strengths', 'Watch out for'], rows: [
        ['Relational', 'Postgres, MySQL', 'Transactions, joins, constraints, flexible queries', 'Scaling writes beyond one primary needs sharding'],
        ['Document', 'MongoDB', 'Nested data read as a whole, flexible schema', 'Cross-document relationships and joins'],
        ['Key-value', 'Redis, DynamoDB', 'Very fast lookups by key at huge scale', 'Queries other than by key'],
        ['Wide-column', 'Cassandra, ScyllaDB', 'Massive write throughput with known query patterns', 'Ad-hoc queries; design tables per query'],
        ['Search', 'Elasticsearch, OpenSearch', 'Full-text search, filters, relevance', 'Not a primary source of truth']
      ] },
      { type: 'callout', variant: 'tip', title: 'A safe default', text: 'For most interview problems, a relational database for core data is a perfectly good starting answer. Introduce other stores for specific needs: Redis for caching and counters, a search engine for text search, object storage for files.' },

      { type: 'h2', text: 'Modelling around queries' },
      { type: 'code', language: 'sql', code: `-- Core query: "show a user's 20 most recent orders"
CREATE TABLE orders (
  id          BIGINT PRIMARY KEY,
  user_id     BIGINT NOT NULL REFERENCES users(id),
  status      TEXT NOT NULL,
  total_paise INTEGER NOT NULL,
  created_at  TIMESTAMPTZ NOT NULL DEFAULT now()
);

CREATE INDEX idx_orders_user_recent ON orders (user_id, created_at DESC);

SELECT id, status, total_paise, created_at
FROM orders WHERE user_id = $1
ORDER BY created_at DESC LIMIT 20;` },
      { type: 'p', text: 'The index matches the query exactly: filter by `user_id`, already sorted by `created_at`. The database reads 20 index entries and stops, no matter how many orders exist.' },

      { type: 'h2', text: 'How indexes work' },
      { type: 'p', text: 'An index is a sorted structure (usually a B-tree) of column values pointing to rows. Without one, the database scans every row; with one, it searches the tree in roughly O(log n) and reads matching rows in order. A compound index on `(a, b, c)` helps queries that filter on `a`, on `a` and `b`, or on all three, but not on `b` alone.' },
      { type: 'table', headers: ['Index type', 'Use it for'], rows: [
        ['B-tree', 'Equality, ranges and sorting (the default)'],
        ['Unique', 'Enforcing uniqueness, e.g. one account per email'],
        ['Composite', 'Queries that filter and sort on several columns'],
        ['Geospatial (e.g. geohash, R-tree)', '“Find things near this point”'],
        ['Inverted / full-text', 'Searching words inside text']
      ] },
      { type: 'tip', text: 'Indexes speed up reads but slow down writes, because every insert and update must also update each index. Index for your important queries, not every column.' },

      { type: 'h2', text: 'Transactions and integrity' },
      { type: 'p', text: 'When several changes must succeed or fail together (debit one account, credit another), use a transaction. For contested resources like the last seat on a flight, prevent double-booking with a unique constraint or a conditional update instead of “check, then write” in application code.' },
      { type: 'code', language: 'sql', code: `-- Reserve a seat only if it is still free; 0 rows updated means someone beat you
UPDATE seats SET held_by = $1, held_until = now() + interval '10 minutes'
WHERE flight_id = $2 AND seat_no = $3 AND held_by IS NULL;` },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Choosing a database by popularity instead of by access patterns.',
        'Forgetting indexes for the main queries in your design.',
        'Check-then-write logic that races under concurrency.',
        'Storing large files in the database instead of object storage.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'When would you choose a relational database over a NoSQL one?', answer: ['When you need transactions across multiple entities, rich queries and joins, or strong integrity constraints, and your scale fits a primary with replicas (which covers most products). Choose NoSQL for specific strengths: huge write volume with known queries, simple key lookups at massive scale, or flexible document shapes.'] },
      { type: 'qa', question: 'Why might a query ignore an index?', answer: ['The query does not use a prefix of the index columns, applies a function to the column, uses a leading wildcard, or matches so many rows that a full scan is cheaper. Checking the query plan shows which.'] },
      { type: 'qa', question: 'How do you prevent two users booking the same seat?', answer: ['Make the database enforce it: a conditional update that only succeeds if the seat is still free, or a unique constraint on (flight, seat) for confirmed bookings. Application-level checks alone race under concurrency.'] },

      { type: 'exercise', title: 'Model a course platform', task: 'Learners enrol in courses and complete lessons. Core queries: (1) list a learner’s enrolled courses with progress, (2) list the lessons in a course in order, (3) mark a lesson complete. Sketch tables and the indexes for each query.', solution: `courses(id PK, title, ...)
lessons(id PK, course_id FK, position, title)
  INDEX (course_id, position)                  -- query 2
enrolments(user_id, course_id, enrolled_at, PRIMARY KEY (user_id, course_id))
  -- query 1 uses the primary key prefix user_id
lesson_completions(user_id, lesson_id, completed_at,
                   PRIMARY KEY (user_id, lesson_id))
  -- query 3: INSERT ... ON CONFLICT DO NOTHING (idempotent)
  -- progress = completions joined with lessons, grouped by course`, language: 'text' },

      { type: 'summary', items: [
        'Start from the main queries, then choose the model and indexes.',
        'A relational database is a strong default; add specialised stores for specific needs.',
        'Compound indexes serve prefixes of their columns; indexes cost write speed.',
        'Let the database enforce invariants with transactions, constraints and conditional updates.'
      ] }
    ]
  },
  {
    slug: 'replication-and-sharding',
    title: 'Replication and Sharding',
    summary: 'Copy data for availability and read scale; split data for write scale. Shard keys, hot spots and consistent hashing.',
    readTime: 12,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'Databases are usually the hardest part of a system to scale and the most painful part to lose. Replication protects you from losing a machine and spreads reads; sharding lets data and writes grow beyond one machine. Knowing when you need each, and what each costs, is central to most deep dives.' },

      { type: 'h2', text: 'The mental model: photocopies vs splitting the filing cabinet' },
      { type: 'p', text: 'Replication is making photocopies of the whole filing cabinet so more people can read at once and a fire does not destroy everything. Sharding is splitting the files across several cabinets, A–H in one and I–P in another, because one cabinet can no longer hold everything or handle all the filing.' },

      { type: 'h2', text: 'Replication' },
      { type: 'p', text: 'In the common primary–replica setup, the primary accepts writes and streams changes to replicas, which serve reads. If the primary fails, a replica is promoted.' },
      {"type": "diagram", "title": "Primary–replica replication", "caption": "Writes go to the primary, which streams changes to replicas (dashed = asynchronous). Reads can be spread across replicas, which may lag slightly behind.", "cols": 3, "rows": 3, "nodes": [{"id": "app", "label": "App servers", "kind": "service", "col": 0, "row": 1}, {"id": "primary", "label": "Primary", "kind": "db", "col": 1, "row": 1, "sub": "all writes"}, {"id": "r1", "label": "Replica 1", "kind": "db", "col": 2, "row": 0, "sub": "reads"}, {"id": "r2", "label": "Replica 2", "kind": "db", "col": 2, "row": 2, "sub": "reads"}], "edges": [{"from": "app", "to": "primary", "label": "writes"}, {"from": "primary", "to": "r1", "label": "replicate", "dashed": true}, {"from": "primary", "to": "r2", "label": "replicate", "dashed": true}, {"from": "app", "to": "r1", "label": "reads"}, {"from": "app", "to": "r2", "label": "reads"}]},
      { type: 'table', headers: ['Mode', 'Benefit', 'Cost'], rows: [
        ['Asynchronous', 'Fast writes', 'Replicas lag; recent writes can be lost if the primary dies'],
        ['Synchronous', 'No data loss on failover', 'Every write waits for a replica'],
        ['Quorum / semi-sync', 'A balance of both', 'More moving parts']
      ] },
      { type: 'callout', variant: 'warning', title: 'Read-your-own-writes', text: 'With async replicas, a user can update their profile and then read the old version from a lagging replica. Common fixes: read from the primary for a few seconds after a user writes, or route a user’s reads to the primary for data they just changed.' },

      { type: 'h2', text: 'Sharding' },
      { type: 'p', text: 'Each shard holds a subset of the data. Every row has a shard key that decides where it lives.' },
      { type: 'table', headers: ['Strategy', 'How', 'Risk'], rows: [
        ['Range', 'user_id 1–1M on shard 1, and so on', 'New data piles onto the newest shard'],
        ['Hash', 'hash(key) decides the shard', 'Range queries hit every shard'],
        ['Directory', 'A lookup service maps keys to shards', 'An extra dependency, but very flexible'],
        ['Geographic / tenant', 'By region or customer', 'One huge tenant can overload a shard']
      ] },
      { type: 'h3', text: 'Choosing a shard key' },
      { type: 'ul', items: [
        'Spreads reads **and** writes evenly (high cardinality, no single dominant value).',
        'Appears in your most common queries, so they touch one shard.',
        'Groups data that is used together: chat messages by `conversation_id`, orders by `user_id`.'
      ] },

      { type: 'h2', text: 'Consistent hashing' },
      { type: 'p', text: 'With `hash(key) % N`, adding one shard changes the result for almost every key, forcing a massive data move. Consistent hashing places shards and keys on a ring; each key belongs to the next shard clockwise. Adding a shard only moves the keys in one segment, about 1/N of the data. Using many virtual points per shard keeps the load even.' },
      { type: 'code', variants: [
        { language: 'java', code: `public class ModuloMove {
    public static void main(String[] args) {
        int moved = 0;
        for (int key = 0; key < 100_000; key++) {
            if (key % 4 != key % 5) moved++;
        }
        System.out.printf("%d%% of keys move%n", Math.round(moved / 1000.0));
    }
}`, output: `80% of keys move` },
        { language: 'python', code: `# How many keys move when going from 4 to 5 shards with modulo hashing?
moved = sum(1 for key in range(100_000) if key % 4 != key % 5)
print(f"{moved / 1000:.0f}% of keys move")`, output: `80% of keys move` },
        { language: 'javascript', code: `// How many keys move when going from 4 to 5 shards with modulo hashing?
let moved = 0;
for (let key = 0; key < 100000; key++) {
  if (key % 4 !== key % 5) moved++;
}
console.log(\`\${(moved / 1000).toFixed(0)}% of keys move\`);`, output: `80% of keys move` }
      ] },
      { type: 'p', text: 'With consistent hashing the same change moves roughly 20% of keys, and only those that now belong to the new shard.' },

      { type: 'h2', text: 'What sharding costs' },
      { type: 'ul', items: [
        'Joins and transactions across shards become application problems.',
        'Globally unique IDs need a scheme (for example Snowflake-style IDs).',
        'Rebalancing is operationally heavy; plan with many logical shards on fewer machines.',
        'Every query without the shard key fans out to all shards.'
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Sharding before trying indexes, caching, replicas and a bigger machine.',
        'Picking a shard key that creates hot spots (country, date, celebrity users).',
        'Forgetting about replication lag when reading from replicas.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'What is the difference between replication and sharding?', answer: ['Replication copies the same data to several machines for availability and read scale. Sharding splits data across machines so each holds part of it, scaling writes and storage. Large systems use both: each shard is itself replicated.'] },
      { type: 'qa', question: 'How do you handle a celebrity whose data overloads one shard?', answer: ['Cache their data heavily, split their hot key into several sub-keys spread across shards and combine on read, or move them to dedicated capacity.'] },
      { type: 'qa', question: 'Why is consistent hashing used?', answer: ['So adding or removing a node moves only a small fraction of keys, instead of nearly all of them with modulo hashing. That keeps caches warm and data migrations small.'] },

      { type: 'exercise', title: 'Pick shard keys', task: 'Choose a shard key for each and justify it: (a) chat messages, (b) e-commerce orders, (c) IoT sensor readings written every second by millions of devices.', solution: `(a) conversation_id: a conversation's messages are read together,
    and writes spread across many conversations
(b) user_id: "my orders" queries hit one shard; spread is even.
    Admin queries across all users go to an analytics copy instead
(c) device_id (hashed), possibly combined with a time bucket:
    writes spread evenly; time-only keys would overload one shard`, language: 'text' },

      { type: 'summary', items: [
        'Replicate for availability and read scale; shard for write and storage scale.',
        'Async replicas lag: design for read-your-own-writes.',
        'A good shard key spreads load and matches your main queries.',
        'Consistent hashing keeps data movement small when shards change.'
      ] }
    ]
  },
  {
    slug: 'consistency-and-cap',
    title: 'Consistency, Availability and the CAP Theorem',
    summary: 'What CAP really says, consistency models from strong to eventual, and how to choose per feature.',
    readTime: 10,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'Once data lives on more than one machine, you must decide what happens when they disagree or cannot talk to each other. Interviewers love asking which guarantee a feature needs, because the answer shapes the whole design: a bank balance and a like counter need very different treatment.' },

      { type: 'h2', text: 'The mental model: two branch offices and a broken phone line' },
      { type: 'p', text: 'Two offices share one customer ledger by phoning each other. When the phone line goes down, each office must choose: keep serving customers with possibly out-of-date numbers (availability), or refuse to serve until the line is back so nobody sees wrong numbers (consistency). That forced choice during a network split is the CAP theorem.' },

      { type: 'h2', text: 'What CAP actually says' },
      { type: 'p', text: 'In a distributed system, when a network partition happens, you must choose between consistency (every read sees the latest write, or gets an error) and availability (every request gets a non-error response, possibly stale). Partitions are not optional in real networks, so the practical choice is per operation: behave CP or AP during a partition.' },
      { type: 'callout', variant: 'note', title: 'Beyond CAP', text: 'Even with no partition, there is a trade-off between latency and consistency: waiting for other replicas to confirm makes reads and writes slower. This is often summarised as PACELC.' },

      { type: 'h2', text: 'Consistency models' },
      { type: 'table', headers: ['Model', 'Guarantee', 'Example use'], rows: [
        ['Strong (linearizable)', 'Behaves like a single copy; reads see the latest completed write', 'Account balances, inventory, unique usernames'],
        ['Causal', 'If B depends on A, everyone sees A before B', 'A reply never appears before its comment'],
        ['Read-your-writes', 'You always see your own updates', 'Profile edits, settings'],
        ['Monotonic reads', 'You never see older data after newer data', 'Scrolling a feed'],
        ['Eventual', 'Replicas converge if updates stop', 'Like counts, view counts, recommendations']
      ] },

      { type: 'h2', text: 'Choosing per feature' },
      { type: 'p', text: 'Real systems mix guarantees. In a ticket booking app:' },
      { type: 'ul', items: [
        '**Seat reservation**: strong consistency. Two people must never get the same seat.',
        '**Seat map shown while browsing**: eventual is fine, as long as booking itself re-checks.',
        '**Event description and photos**: heavily cached, minutes of staleness are acceptable.',
        '**“Tickets remaining” counter**: approximate is fine for display.'
      ] },
      { type: 'tip', text: 'A strong interview pattern: “I’ll keep the critical write strongly consistent and let everything around it be eventually consistent and cached.” It shows you know where correctness matters and where speed matters.' },

      { type: 'h2', text: 'Quorums' },
      { type: 'p', text: 'With N replicas, if a write must reach W of them and a read must ask R of them, and R + W > N, every read overlaps at least one replica with the latest write. For example, N = 3, W = 2, R = 2. Lowering W or R makes operations faster and more available, at the risk of stale reads.' },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Saying a system is “CA”: in a distributed system you cannot opt out of partitions.',
        'Applying one consistency level to the entire product.',
        'Promising strong consistency across regions without discussing the latency cost.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'Is your design CP or AP?', answer: ['Answer per feature. For example: “Bookings are CP: during a partition we would rather reject a booking than double-sell. The catalogue and search are AP: serving slightly stale results is better than failing.”'] },
      { type: 'qa', question: 'How do you give users read-your-writes with async replicas?', answer: ['Route a user’s reads to the primary for a short window after they write, or track the write’s version and only read from replicas that have caught up to it.'] },
      { type: 'qa', question: 'How do AP systems resolve conflicting writes?', answer: ['Options include last-write-wins (simple, can lose updates), keeping all versions for the application to merge, and data types designed to merge automatically (CRDTs) such as counters and sets.'] },

      { type: 'exercise', title: 'Assign consistency levels', task: 'For a food delivery app, choose a consistency model for: (1) order payment status, (2) restaurant menu, (3) delivery partner’s live location, (4) number of reviews on a restaurant. Justify each in one line.', solution: `1. Payment status → strong: never show "paid" incorrectly or charge twice
2. Menu → eventual + cached: a few minutes stale is fine; re-check price at checkout
3. Live location → eventual, latest-wins: freshness matters, history doesn't
4. Review count → eventual / approximate: display only`, language: 'text' },

      { type: 'summary', items: [
        'CAP: during a partition, choose consistency or availability, per operation.',
        'Even without partitions, stronger consistency costs latency.',
        'Use strong consistency for critical writes, eventual for the rest.',
        'Quorums (R + W > N) are one way to tune the trade-off.'
      ] }
    ]
  },
  {
    slug: 'queues-and-streams',
    title: 'Queues, Streams and Asynchronous Processing',
    summary: 'Decoupling slow work, absorbing spikes, delivery guarantees, idempotency and the outbox pattern.',
    readTime: 11,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'Many tasks do not need to finish before you respond to the user: sending emails, generating thumbnails, updating search indexes, fanning out notifications. Doing them inside the request makes everything slow and fragile. Queues and streams let you hand work off, absorb traffic spikes and connect services without tight coupling.' },

      { type: 'h2', text: 'The mental model: a restaurant order rail' },
      { type: 'p', text: 'The waiter does not cook your food while you wait at the counter. They pin your order on a rail and go back to taking orders. Cooks take orders from the rail at their own pace. If a rush comes, the rail gets longer, but the waiter keeps working. That rail is a queue.' },

      { type: 'h2', text: 'Queues vs streams' },
      { type: 'table', headers: ['', 'Message queue', 'Event stream (log)'], rows: [
        ['Examples', 'RabbitMQ, Amazon SQS', 'Kafka, Kinesis'],
        ['Consumption', 'Each message goes to one worker, then is deleted', 'Messages are kept; many consumer groups read independently'],
        ['Replay', 'No', 'Yes, by rewinding the read position'],
        ['Ordering', 'Per queue (FIFO variants)', 'Per partition'],
        ['Typical use', 'Background jobs and tasks', 'Event-driven systems, analytics, change data capture']
      ] },

      { type: 'h2', text: 'The basic pattern' },
      { type: 'code', variants: [
        { language: 'java', code: `// API: respond fast, hand off the slow part (Spring Boot + RabbitMQ)
@PostMapping("/videos")
public ResponseEntity<VideoStatus> upload(@AuthenticationPrincipal User user) {
    Video video = videos.save(new Video(user.getId(), "processing"));
    rabbit.convertAndSend("transcode", new TranscodeJob(video.getId()));
    return ResponseEntity.accepted().body(new VideoStatus(video.getId(), "processing"));
}

// Worker: processes jobs at its own pace, scaled independently
@RabbitListener(queues = "transcode")
public void transcode(TranscodeJob job) {
    Video video = videos.findById(job.videoId()).orElseThrow();
    if ("ready".equals(video.getStatus())) return;        // idempotent: safe to run twice
    transcoder.transcodeAll(video);
    video.setStatus("ready");
    videos.save(video);
}` },
        { language: 'python', code: `# API: respond fast, hand off the slow part (FastAPI + Celery)
@app.post("/videos", status_code=202)
def upload_video(user=Depends(current_user)):
    video = db.videos.create(owner_id=user.id, status="processing")
    transcode.delay(video.id)                         # enqueue the job
    return {"id": video.id, "status": "processing"}

# Worker: processes jobs at its own pace, scaled independently
@celery_app.task
def transcode(video_id):
    video = db.videos.find_by_id(video_id)
    if video.status == "ready":
        return                                        # idempotent: safe to run twice
    transcode_all(video)
    db.videos.update(video_id, status="ready")` },
        { language: 'javascript', code: `// API: respond fast, hand off the slow part
app.post('/videos', auth, async (req, res) => {
  const video = await db.videos.create({ ownerId: req.user.id, status: 'processing' });
  await queue.add('transcode', { videoId: video.id });
  res.status(202).json({ id: video.id, status: 'processing' });
});

// Worker: processes jobs at its own pace, scaled independently
worker.process('transcode', async ({ videoId }) => {
  const video = await db.videos.findById(videoId);
  if (video.status === 'ready') return;          // idempotent: safe to run twice
  await transcodeAll(video);
  await db.videos.update(videoId, { status: 'ready' });
});` }
      ] },

      {"type": "diagram", "title": "Handing slow work to a queue", "caption": "The API saves the request and enqueues a job, then responds immediately. Workers process jobs at their own pace; jobs that keep failing move to a dead-letter queue for investigation.", "cols": 4, "rows": 2, "nodes": [{"id": "client", "label": "Client", "kind": "client", "col": 0, "row": 0}, {"id": "api", "label": "API", "kind": "service", "col": 1, "row": 0, "sub": "responds 202"}, {"id": "db", "label": "Database", "kind": "db", "col": 1, "row": 1}, {"id": "q", "label": "Job queue", "kind": "queue", "col": 2, "row": 0}, {"id": "w", "label": "Workers", "kind": "worker", "col": 3, "row": 0, "sub": "idempotent"}, {"id": "dlq", "label": "Dead-letter queue", "kind": "queue", "col": 3, "row": 1}], "edges": [{"from": "client", "to": "api", "label": "upload"}, {"from": "api", "to": "db", "label": "save"}, {"from": "api", "to": "q", "label": "enqueue", "dashed": true}, {"from": "q", "to": "w", "dashed": true}, {"from": "w", "to": "dlq", "label": "after N failures", "dashed": true}]},
      { type: 'h2', text: 'Delivery guarantees and idempotency' },
      { type: 'p', text: 'Most systems deliver messages **at least once**: if a worker crashes after doing the work but before acknowledging it, the message is delivered again. So consumers must be **idempotent**: processing the same message twice must have the same effect as processing it once.' },
      { type: 'ul', items: [
        'Check a status before acting (“already sent? skip”).',
        'Use unique constraints keyed by the message or operation ID.',
        'Pass idempotency keys to external APIs such as payment providers.'
      ] },

      { type: 'h2', text: 'Retries and dead-letter queues' },
      { type: 'p', text: 'Failed jobs should be retried with exponential backoff, a few times. A job that keeps failing (a poison message) must not block the queue forever: after the last retry, move it to a dead-letter queue, alert someone, and keep processing the rest.' },

      { type: 'h2', text: 'The dual-write problem and the outbox pattern' },
      { type: 'p', text: 'If you save an order to the database and then publish an “order created” event, a crash between the two leaves them inconsistent. The outbox pattern fixes it: write the order and an outbox row in one database transaction; a separate relay publishes outbox rows to the queue and marks them sent.' },
      { type: 'code', language: 'sql', code: `BEGIN;
INSERT INTO orders (id, user_id, total_paise) VALUES ($1, $2, $3);
INSERT INTO outbox (id, topic, payload) VALUES ($4, 'order.created', $5);
COMMIT;
-- A relay process reads unsent outbox rows, publishes them, marks them sent.` },

      { type: 'h2', text: 'Ordering' },
      { type: 'p', text: 'Global ordering is expensive and rarely needed. Usually you need order per entity: all events for one order, or all messages in one conversation. Use that entity’s ID as the partition key so its events land in the same partition and are processed in sequence, while different entities are processed in parallel.' },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Assuming exactly-once delivery end to end.',
        'Non-idempotent consumers that send duplicate emails or charges.',
        'Using a queue where the user needs the answer immediately.',
        'No monitoring of queue length and message age.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'When should work go to a queue instead of happening in the request?', answer: ['When the user does not need the result to continue (emails, thumbnails, reports), when the work is slow or bursty, or when downstream systems need protection from spikes. Return 202 Accepted and notify or let the client poll for completion.'] },
      { type: 'qa', question: 'How do you guarantee an event is published if the database write succeeds?', answer: ['Use the transactional outbox pattern: write the event to an outbox table in the same transaction as the data, and have a relay publish it. Change data capture from the database log is an alternative.'] },
      { type: 'qa', question: 'Kafka or a simple queue?', answer: ['A simple queue for background jobs where each task is done once by one worker. Kafka-style streams when many consumers need the same events, you need replay, or you are building event-driven pipelines and analytics.'] },

      { type: 'exercise', title: 'Make the consumer idempotent', task: 'A worker handles “order.paid” events by sending a confirmation email and adding loyalty points. Events may be delivered twice. Describe how you would make both actions safe to repeat.', solution: `Loyalty points:
  INSERT INTO loyalty_ledger (order_id, user_id, points) ...
  with UNIQUE(order_id): the second insert fails harmlessly
Email:
  UPDATE orders SET confirmation_sent_at = now()
  WHERE id = $1 AND confirmation_sent_at IS NULL;
  send the email only if exactly 1 row was updated
  (and pass the order id as an idempotency key to the email provider)`, language: 'text' },

      { type: 'summary', items: [
        'Queues decouple slow work and absorb spikes; streams add replay and many consumers.',
        'Delivery is at least once, so consumers must be idempotent.',
        'Retry with backoff; move poison messages to a dead-letter queue.',
        'Use the outbox pattern to publish events reliably with database writes.'
      ] }
    ]
  },
  {
    slug: 'real-time-updates',
    title: 'Real-Time Updates: Polling, SSE and WebSockets',
    summary: 'How servers push updates to clients, how to choose a technique, and how to scale live connections.',
    readTime: 10,
    content: [
      { type: 'h2', text: 'Why this matters' },
      { type: 'p', text: 'Chat, live scores, ride tracking, notifications, collaborative editing and streaming AI responses all need the server to push updates to the client. HTTP was designed for the client to ask, so you need a technique for the server to tell. Choosing one, and scaling it, is a common deep dive.' },

      { type: 'h2', text: 'The mental model: checking the mailbox vs a phone call' },
      { type: 'p', text: 'Polling is walking to the mailbox every few minutes to see if anything arrived. Long polling is waiting at the mailbox until something arrives. Server-Sent Events is a one-way radio broadcast you tune into. WebSockets are a phone call: both sides can talk at any time.' },

      { type: 'h2', text: 'The options' },
      { type: 'table', headers: ['Technique', 'Direction', 'Strengths', 'Weaknesses'], rows: [
        ['Short polling', 'Client asks every N seconds', 'Simplest, works everywhere', 'Wasteful; updates delayed up to N seconds'],
        ['Long polling', 'Server holds the request until there is news', 'Near real-time over plain HTTP', 'Reconnect overhead after every message'],
        ['Server-Sent Events', 'Server → client stream over HTTP', 'Simple, auto-reconnect, works with HTTP/2', 'One direction only; text only'],
        ['WebSockets', 'Both ways, persistent connection', 'Lowest latency, bidirectional, binary', 'Stateful connections are harder to scale and load-balance']
      ] },

      { type: 'h2', text: 'Choosing' },
      { type: 'ul', items: [
        'Updates every few minutes, or simplicity matters most: **polling**.',
        'Server pushes, client rarely sends (notifications, live scores, AI token streaming): **SSE**.',
        'Both sides send frequently (chat, games, collaborative editing): **WebSockets**.'
      ] },
      { type: 'code', variants: [
        { language: 'java', code: `// Server-Sent Events in Spring Boot
@GetMapping(path = "/events", produces = MediaType.TEXT_EVENT_STREAM_VALUE)
public SseEmitter events(@AuthenticationPrincipal User user) {
    SseEmitter emitter = new SseEmitter(0L);                        // no server-side timeout
    Runnable unsubscribe = notifications.subscribe(user.getId(), notification -> {
        try {
            emitter.send(SseEmitter.event().name("notification").data(notification));
        } catch (IOException e) {
            emitter.completeWithError(e);                           // client went away
        }
    });
    emitter.onCompletion(unsubscribe);
    emitter.onTimeout(unsubscribe);
    return emitter;
}

// Browser: new EventSource("/events").addEventListener("notification", ...)` },
        { language: 'python', code: `# Server-Sent Events in FastAPI
@app.get("/events")
async def events(request: Request, user=Depends(current_user)):
    async def stream():
        async for notification in notifications.subscribe(user.id):
            if await request.is_disconnected():
                break
            yield f"event: notification\\ndata: {json.dumps(notification)}\\n\\n"

    return StreamingResponse(stream(), media_type="text/event-stream")

# Browser: new EventSource("/events").addEventListener("notification", ...)` },
        { language: 'javascript', code: `// Server-Sent Events in Express
app.get('/events', auth, (req, res) => {
  res.set({ 'Content-Type': 'text/event-stream', 'Cache-Control': 'no-cache', Connection: 'keep-alive' });
  res.flushHeaders();

  const send = (event, data) => res.write(\`event: \${event}\\ndata: \${JSON.stringify(data)}\\n\\n\`);
  const unsubscribe = notifications.subscribe(req.user.id, (n) => send('notification', n));
  const heartbeat = setInterval(() => res.write(': ping\\n\\n'), 25000);   // keep proxies from closing it

  req.on('close', () => { clearInterval(heartbeat); unsubscribe(); });
});

// Browser
const source = new EventSource('/events', { withCredentials: true });
source.addEventListener('notification', (e) => showToast(JSON.parse(e.data)));` }
      ] },

      { type: 'h2', text: 'Scaling live connections' },
      { type: 'p', text: 'The hard part is not opening connections; it is delivering a message to a user whose connection lives on a different server.' },
      { type: 'code', language: 'text', code: `User A ──ws──► Gateway 1                 Gateway 7 ◄──ws── User B
                  │  publish "to B"          ▲
                  └──────► Pub/sub (Redis) ───┘  Gateway 7 is subscribed for B

Connection registry: user B → gateway 7 (with a TTL, refreshed by heartbeats)` },
      { type: 'ul', items: [
        'Keep connections on dedicated gateway servers; keep business logic in normal stateless services.',
        'Use a pub/sub layer or a registry of which gateway holds which user to route messages.',
        'Expect reconnect storms after deploys: clients should reconnect with random backoff.',
        'Clients must catch up on missed messages after reconnecting, using a cursor or sequence number.'
      ] },

      { type: 'h2', text: 'Common mistakes' },
      { type: 'ul', items: [
        'Choosing WebSockets for one-way updates where SSE is simpler.',
        'Assuming a message sent while the client was disconnected was received.',
        'Putting heavy business logic inside the connection servers.',
        'Forgetting load balancer idle timeouts, which silently drop quiet connections.'
      ] },

      { type: 'h2', text: 'Interview questions' },
      { type: 'qa', question: 'Why not just use polling every second?', answer: ['With many users it multiplies load: a million clients polling every second is a million requests per second, mostly returning nothing. It is acceptable for small scale or infrequent updates, but push techniques are far cheaper at scale.'] },
      { type: 'qa', question: 'How does a message reach a user connected to another server?', answer: ['Publish it to a pub/sub channel that all gateways subscribe to (or look up the user’s gateway in a registry and send it directly). The gateway holding the user’s connection delivers it.'] },
      { type: 'qa', question: 'What happens to messages while a user is offline?', answer: ['They must be stored durably. On reconnect, the client asks for everything after the last sequence number it saw, and a push notification can alert the user in the meantime.'] },

      { type: 'exercise', title: 'Pick the technique', task: 'Choose polling, SSE or WebSockets for each and explain why: (1) a cricket score widget, (2) a multiplayer drawing board, (3) an order status page that changes 4–5 times per order, (4) streaming an AI assistant’s answer.', solution: `1. SSE: server pushes score updates, client sends nothing
2. WebSockets: every participant sends and receives strokes constantly
3. Polling every 10–30s, or SSE: updates are rare; polling is simplest
4. SSE: one-way token stream from server to client, auto-reconnect`, language: 'text' },

      { type: 'summary', items: [
        'Polling is simple, SSE is one-way push, WebSockets are two-way.',
        'Pick the simplest technique that meets the latency and direction needs.',
        'Route messages across connection servers with pub/sub or a registry.',
        'Persist messages and let clients catch up after reconnecting.'
      ] }
    ]
  }
];

// Order: method and core building blocks, then advanced topics, then case studies
export const systemDesignLessons = [
  ...fundamentals,
  ...systemDesignCore2,
  ...systemDesignCore3,
  ...systemDesignCaseStudies,
  ...systemDesignCaseStudies2,
  ...systemDesignCaseStudies3
];
